import { type FormEvent, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Loader } from '@lms/ui';
import { PERMISSIONS } from '@lms/shared-types';
import { HubDetailPageHeader } from '@/components/layout/HubDetailPageHeader';
import { MediaPicker, type MediaPickerValue } from '@/components/media/MediaPicker';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { openAuthenticatedMedia } from '@/lib/authenticated-media';
import {
  libraryBackLabel,
  libraryBackUrl,
  libraryCrossEntryHref,
  libraryEditUrl,
  libraryPreviewUrl,
} from '@/lib/library-navigation';
import {
  LIBRARY_STATUS_BADGE,
  LIBRARY_STATUS_LABELS,
  LIBRARY_TYPE_LABELS,
} from './library-labels';
import { PdfFolderField, type PdfFolderFile } from './PdfFolderField';

interface LibraryMediaFile {
  id: string;
  name: string;
  originalName: string;
  mimeType: string;
  humanSize: string;
  url: string;
}

interface LibraryVersionRow {
  id: string;
  versionNumber: number;
  versionLabel: string;
  createdAt: string;
  mediaFile: LibraryMediaFile | null;
  mediaFiles?: (LibraryMediaFile & { title?: string })[];
  externalUrl: string | null;
  hasTextContent: boolean;
}

interface LibraryShowDetail {
  id: string;
  title: string;
  type: string;
  status: string;
  description?: string;
  language?: string;
  tags?: string[];
  versionCount?: number;
  usageCount?: number;
  hasRenderableContent?: boolean;
  author?: { id: string; fullName: string } | null;
  currentMediaFile?: LibraryMediaFile | null;
  files?: (PdfFolderFile & { mediaUrl?: string })[];
  externalUrl?: string;
  h5pPackageId?: string | null;
  assessmentId?: string | null;
  currentVersion?: { versionLabel: string } | null;
  versions?: LibraryVersionRow[];
}

const MEDIA_TYPES_WITHOUT_CONTENT = ['video', 'pdf', 'pdf_folder', 'audio', 'image', 'download'];

function formatVersionDate(value: string) {
  return new Date(value).toLocaleString('es', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function LibraryShowPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(PERMISSIONS.BIBLIOTECA_EDITAR);

  const backUrl = libraryBackUrl(searchParams);
  const backLabel = libraryBackLabel(searchParams);
  const previewUrl = id ? libraryPreviewUrl(id, searchParams) : '/library';
  const editUrl = id ? libraryEditUrl(id, searchParams) : '/library';

  const headerActions = (
    <>
      <Link to={previewUrl} className="lib-show-btn lib-show-btn--outline">
        <span className="material-symbols-outlined text-xl" aria-hidden="true">
          visibility
        </span>
        Vista previa
      </Link>
      {canEdit && (
        <Link to={editUrl} className="lib-show-btn lib-show-btn--primary">
          <span className="material-symbols-outlined text-xl" aria-hidden="true">
            edit
          </span>
          Editar
        </Link>
      )}
    </>
  );

  const [versionLabel, setVersionLabel] = useState('');
  const [versionContent, setVersionContent] = useState('');
  const [versionExternalUrl, setVersionExternalUrl] = useState('');
  const [versionMediaFile, setVersionMediaFile] = useState<MediaPickerValue | null>(null);
  const [folderFiles, setFolderFiles] = useState<PdfFolderFile[]>([]);
  const [formError, setFormError] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['library', id],
    queryFn: () => api<LibraryShowDetail>(`/library/${id}`),
    enabled: Boolean(id),
  });

  const pageTitle = data?.title ?? 'Objeto de aprendizaje';

  useEffect(() => {
    if (data?.type !== 'pdf_folder') return;
    setFolderFiles((data.files ?? []).map((file) => ({ mediaFileId: file.mediaFileId, title: file.title })));
  }, [data]);

  const replaceVersion = useMutation({
    mutationFn: (body: {
      label?: string;
      content?: string;
      externalUrl?: string;
      mediaFileId?: string | null;
      files?: PdfFolderFile[];
    }) => api<LibraryShowDetail>(`/library/${id}/version`, { method: 'POST', body }),
    onSuccess: () => {
      setFormError(null);
      setVersionLabel('');
      setVersionContent('');
      setVersionExternalUrl('');
      setVersionMediaFile(null);
      queryClient.invalidateQueries({ queryKey: ['library', id] });
      queryClient.invalidateQueries({ queryKey: ['library'] });
    },
    onError: (err: Error) => setFormError(err.message),
  });

  const statusBadge = LIBRARY_STATUS_BADGE[data?.status ?? 'draft'] ?? LIBRARY_STATUS_BADGE.draft;
  const typeLabel = LIBRARY_TYPE_LABELS[data?.type ?? ''] ?? data?.type ?? '—';
  const statusLabel = LIBRARY_STATUS_LABELS[data?.status ?? ''] ?? data?.status ?? '—';
  const courseCount = data?.usageCount ?? 0;
  const versionCount = data?.versionCount ?? data?.versions?.length ?? 0;
  const showMissingContentWarning =
    data &&
    !data.hasRenderableContent &&
    MEDIA_TYPES_WITHOUT_CONTENT.includes(data.type);

  const canReplaceVersion = canEdit && data && !data.h5pPackageId && !data.assessmentId;

  function handleReplaceVersion(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (data?.type === 'pdf_folder' && folderFiles.length === 0) {
      setFormError('Agrega al menos un PDF a la carpeta.');
      return;
    }
    replaceVersion.mutate({
      label: versionLabel.trim() || undefined,
      content: data?.type === 'pdf_folder' ? undefined : versionContent.trim() || undefined,
      externalUrl: data?.type === 'pdf_folder' ? undefined : versionExternalUrl.trim() || undefined,
      mediaFileId: data?.type === 'pdf_folder' ? null : versionMediaFile?.id ?? null,
      files: data?.type === 'pdf_folder' ? folderFiles : undefined,
    });
  }

  return (
    <div className="lms-library-show lms-site-admin-hub-page font-sans space-y-8">
      <HubDetailPageHeader
        backTo={backUrl}
        backLabel={backLabel}
        title={pageTitle}
        description="Detalle del objeto de aprendizaje"
        actions={headerActions}
      />

      {error && <Alert variant="danger">{(error as Error).message}</Alert>}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : data ? (
        <>
          <div className="flex flex-wrap gap-3">
            <span className={statusBadge.className}>
              <span
                className="material-symbols-outlined text-base"
                style={statusBadge.fill ? { fontVariationSettings: "'FILL' 1" } : undefined}
                aria-hidden="true"
              >
                {statusBadge.icon}
              </span>
              {statusLabel}
            </span>
            <span className="lib-show-badge lib-show-badge--type">
              <span className="material-symbols-outlined text-base" aria-hidden="true">
                description
              </span>
              {typeLabel}
            </span>
            <span className="lib-show-badge lib-show-badge--muted">Versión v{versionCount}</span>
            <span className="lib-show-badge lib-show-badge--muted">
              {courseCount} {courseCount === 1 ? 'curso en uso' : 'cursos en uso'}
            </span>
          </div>

          {showMissingContentWarning && (
            <Alert variant="warning">
              <p className="font-medium">Sin contenido asociado</p>
              <p className="mt-1">
                Este objeto aún no tiene archivo vinculado. Sube el archivo a{' '}
                <Link to="/media" className="font-medium underline">
                  Multimedia
                </Link>{' '}
                y publícalo en{' '}
                <a href="#reemplazar-version" className="font-medium underline">
                  Reemplazar versión
                </a>
                .
              </p>
            </Alert>
          )}

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="space-y-6 lg:col-span-1">
              <section className="lib-show-card">
                <div className="lib-show-card__head">
                  <span className="material-symbols-outlined" aria-hidden="true">
                    attachment
                  </span>
                  <h2>Archivo</h2>
                </div>

                {data.type === 'pdf_folder' ? (
                  data.files?.length ? (
                    <ul className="space-y-3">
                      {data.files.map((file, index) => (
                        <li key={file.mediaFileId} className="flex items-center justify-between gap-3 text-sm">
                          <span className="min-w-0">
                            <span className="mr-2 font-medium text-dash-on-surface-variant">{index + 1}.</span>
                            {file.title || 'PDF'}
                          </span>
                          {file.mediaUrl ? (
                            <button
                              type="button"
                              className="shrink-0 font-medium text-dash-primary hover:underline"
                              onClick={() => void openAuthenticatedMedia(file.mediaUrl!, file.title || 'PDF')}
                            >
                              Abrir
                            </button>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-dash-on-surface-variant">Sin PDF en la carpeta.</p>
                  )
                ) : data.currentMediaFile ? (
                  <>
                    <dl className="space-y-4">
                      <div>
                        <dt className="lib-show-meta-label">Nombre</dt>
                        <dd className="lib-show-meta-value">
                          {data.currentMediaFile.originalName || data.currentMediaFile.name}
                        </dd>
                      </div>
                      <div>
                        <dt className="lib-show-meta-label">Tamaño</dt>
                        <dd className="lib-show-meta-value">{data.currentMediaFile.humanSize}</dd>
                      </div>
                      <div>
                        <dt className="lib-show-meta-label">Formato</dt>
                        <dd className="lib-show-meta-value">{data.currentMediaFile.mimeType}</dd>
                      </div>
                    </dl>
                    <button
                      type="button"
                      className="lib-show-open-btn mt-4"
                      onClick={() =>
                        void openAuthenticatedMedia(
                          data.currentMediaFile!.url,
                          data.currentMediaFile!.originalName || data.currentMediaFile!.name,
                        )
                      }
                    >
                      <span className="material-symbols-outlined text-xl" aria-hidden="true">
                        open_in_new
                      </span>
                      Abrir URL
                    </button>
                  </>
                ) : data.externalUrl ? (
                  <>
                    <dl className="space-y-4">
                      <div>
                        <dt className="lib-show-meta-label">Enlace externo</dt>
                        <dd className="lib-show-meta-value break-all">{data.externalUrl}</dd>
                      </div>
                    </dl>
                    <a
                      href={data.externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="lib-show-open-btn mt-4"
                    >
                      <span className="material-symbols-outlined text-xl" aria-hidden="true">
                        open_in_new
                      </span>
                      Abrir URL
                    </a>
                  </>
                ) : data.h5pPackageId ? (
                  <>
                    <p className="text-sm text-dash-on-surface-variant">Actividad H5P vinculada.</p>
                    <Link
                      to={id ? libraryCrossEntryHref(`/h5p/${data.h5pPackageId}`, id, searchParams) : `/h5p/${data.h5pPackageId}`}
                      className="lib-show-open-btn mt-4"
                    >
                      <span className="material-symbols-outlined text-xl" aria-hidden="true">
                        extension
                      </span>
                      Abrir en Biblioteca H5P
                    </Link>
                  </>
                ) : data.assessmentId ? (
                  <>
                    <p className="text-sm text-dash-on-surface-variant">Evaluación vinculada.</p>
                    <Link
                      to={id ? libraryCrossEntryHref(`/evaluaciones/${data.assessmentId}`, id, searchParams) : `/evaluaciones/${data.assessmentId}`}
                      className="lib-show-open-btn mt-4"
                    >
                      <span className="material-symbols-outlined text-xl" aria-hidden="true">
                        quiz
                      </span>
                      Abrir evaluación
                    </Link>
                  </>
                ) : (
                  <p className="text-sm text-dash-on-surface-variant">Sin archivo asociado.</p>
                )}
              </section>

              <section className="lib-show-card">
                <div className="lib-show-card__head">
                  <span className="material-symbols-outlined" aria-hidden="true">
                    info
                  </span>
                  <h2>Metadatos</h2>
                </div>
                <dl className="grid gap-4">
                  <div>
                    <dt className="lib-show-meta-label">Autor</dt>
                    <dd className="lib-show-meta-value">{data.author?.fullName ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="lib-show-meta-label">Categoría</dt>
                    <dd className="lib-show-meta-value text-dash-on-surface-variant">—</dd>
                  </div>
                  <div>
                    <dt className="lib-show-meta-label">Idioma</dt>
                    <dd className="lib-show-meta-value">
                      {data.language ? data.language.toUpperCase() : '—'}
                    </dd>
                  </div>
                  <div>
                    <dt className="lib-show-meta-label">Etiquetas</dt>
                    <dd
                      className={`lib-show-meta-value ${data.tags?.length ? '' : 'text-dash-on-surface-variant'}`}
                    >
                      {data.tags?.length ? data.tags.join(', ') : '—'}
                    </dd>
                  </div>
                  {data.description ? (
                    <div>
                      <dt className="lib-show-meta-label">Descripción</dt>
                      <dd className="lib-show-meta-value">{data.description}</dd>
                    </div>
                  ) : null}
                </dl>
              </section>
            </div>

            <div className="lg:col-span-2">
              <section className="lib-show-card lib-show-card--flush overflow-hidden">
                <div className="lib-show-versions-head">
                  <h2 className="lib-show-versions-title">Versiones ({data.versions?.length ?? 0})</h2>
                  {data.currentVersion ? (
                    <span className="lib-show-current-pill">{data.currentVersion.versionLabel} Actual</span>
                  ) : null}
                </div>

                <div className="space-y-8 p-6">
                  <div className="overflow-hidden rounded-lg border border-dash-outline-variant">
                    <div className="overflow-x-auto">
                      <table className="w-full text-left">
                        <thead className="bg-[#e8e8ea]">
                          <tr>
                            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-dash-on-surface-variant">
                              Versión
                            </th>
                            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-dash-on-surface-variant">
                              Fecha
                            </th>
                            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-dash-on-surface-variant">
                              Archivo
                            </th>
                            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-dash-on-surface-variant">
                              Acciones
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-dash-outline-variant bg-white">
                          {data.versions?.length ? (
                            data.versions.map((version) => (
                              <tr key={version.id} className="transition-colors hover:bg-[#f3f3f6]">
                                <td className="px-4 py-4 font-medium text-dash-on-surface">
                                  {version.versionLabel}
                                </td>
                                <td className="px-4 py-4 text-sm text-dash-on-surface-variant">
                                  {formatVersionDate(version.createdAt)}
                                </td>
                                <td className="px-4 py-4">
                                  {version.mediaFiles?.length ? (
                                    <span className="text-sm text-dash-on-surface-variant">
                                      {version.mediaFiles.length} PDF
                                    </span>
                                  ) : version.mediaFile ? (
                                    <div className="flex items-center gap-2 text-sm">
                                      <span
                                        className="material-symbols-outlined text-xl text-dash-secondary"
                                        aria-hidden="true"
                                      >
                                        picture_as_pdf
                                      </span>
                                      <span>
                                        {version.mediaFile.originalName || version.mediaFile.name}
                                      </span>
                                    </div>
                                  ) : version.externalUrl ? (
                                    <span className="text-sm text-dash-on-surface-variant">Enlace externo</span>
                                  ) : version.hasTextContent ? (
                                    <span className="text-sm text-dash-on-surface-variant">Texto / HTML</span>
                                  ) : (
                                    <span className="text-sm text-dash-on-surface-variant">—</span>
                                  )}
                                </td>
                                <td className="px-4 py-4">
                                  {version.mediaFile ? (
                                    <button
                                      type="button"
                                      className="text-sm font-medium text-dash-primary hover:underline"
                                      onClick={() =>
                                        void openAuthenticatedMedia(
                                          version.mediaFile!.url,
                                          version.mediaFile!.originalName || version.mediaFile!.name,
                                          true,
                                        )
                                      }
                                    >
                                      Descargar
                                    </button>
                                  ) : version.externalUrl ? (
                                    <a
                                      href={version.externalUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-sm font-medium text-dash-primary hover:underline"
                                    >
                                      Abrir
                                    </a>
                                  ) : (
                                    <span className="text-sm text-dash-on-surface-variant">—</span>
                                  )}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={4} className="px-4 py-8 text-center text-sm text-dash-on-surface-variant">
                                No hay versiones registradas.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {canReplaceVersion ? (
                    <div id="reemplazar-version" className="space-y-6">
                      <div className="flex items-start gap-3 rounded-lg border border-[#68abff] bg-[#d4e3ff] p-4 text-[#004883]">
                        <span className="material-symbols-outlined shrink-0" aria-hidden="true">
                          info
                        </span>
                        <p className="text-base italic leading-relaxed">
                          Nota: Al publicar una nueva versión, todos los cursos que utilicen este objeto se
                          actualizarán automáticamente.
                        </p>
                      </div>

                      <div className="border-t border-dash-outline-variant pt-6">
                        <h3 className="mb-6 text-2xl font-semibold text-dash-on-surface">Reemplazar versión</h3>
                        {formError && (
                          <Alert variant="danger" className="mb-4">
                            {formError}
                          </Alert>
                        )}
                        <form
                          onSubmit={handleReplaceVersion}
                          className="grid grid-cols-1 gap-6 md:grid-cols-2"
                        >
                          <div>
                            <label htmlFor="version-label" className="lib-show-meta-label mb-2 block">
                              Etiqueta de versión
                            </label>
                            <input
                              id="version-label"
                              type="text"
                              value={versionLabel}
                              onChange={(event) => setVersionLabel(event.target.value)}
                              placeholder="Ej: v2, Final, Revisado..."
                              className="lib-show-input"
                            />
                          </div>

                          {data.type === 'pdf_folder' ? (
                            <div className="md:col-span-2">
                              <PdfFolderField files={folderFiles} onChange={setFolderFiles} />
                            </div>
                          ) : (
                          <div>
                            <label className="lib-show-meta-label mb-2 block">Nuevo archivo</label>
                            <div className="lib-show-version-picker">
                              <MediaPicker label="" value={versionMediaFile} onChange={setVersionMediaFile} />
                            </div>
                          </div>
                          )}

                          {data.type !== 'pdf_folder' && (
                          <>
                          <div className="md:col-span-2">
                            <label htmlFor="version-content" className="lib-show-meta-label mb-2 block">
                              Texto / HTML
                            </label>
                            <textarea
                              id="version-content"
                              rows={4}
                              value={versionContent}
                              onChange={(event) => setVersionContent(event.target.value)}
                              placeholder="Contenido adicional o notas sobre los cambios..."
                              className="lib-show-input lib-show-input--textarea"
                            />
                          </div>

                          <div className="md:col-span-2">
                            <label htmlFor="version-external-url" className="lib-show-meta-label mb-2 block">
                              Enlace externo
                            </label>
                            <div className="relative">
                              <span
                                className="material-symbols-outlined pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-dash-on-surface-variant"
                                aria-hidden="true"
                              >
                                link
                              </span>
                              <input
                                id="version-external-url"
                                type="url"
                                value={versionExternalUrl}
                                onChange={(event) => setVersionExternalUrl(event.target.value)}
                                placeholder="https://ejemplo.com/recurso"
                                className="lib-show-input !pl-10"
                              />
                            </div>
                          </div>
                          </>
                          )}

                          <div className="md:col-span-2 pt-2">
                            <button
                              type="submit"
                              className="lib-show-publish-btn"
                              disabled={replaceVersion.isPending}
                            >
                              <span
                                className="material-symbols-outlined"
                                style={{ fontVariationSettings: "'FILL' 1" }}
                                aria-hidden="true"
                              >
                                publish
                              </span>
                              {replaceVersion.isPending ? 'Publicando…' : 'Publicar nueva versión'}
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  ) : null}
                </div>
              </section>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
